import { PhotoAnnotation, PhotoQuery, PhotoStats, PhotoUploadData, ProjectPhoto } from '../types';
import api from './api';
import configService from './configService';

class PhotoService {
  // Helper method to determine the correct API endpoint based on context
  private getPhotosEndpoint(projectId?: string): string {
    if (projectId) {
      return `/projects/${projectId}/photos`;
    }
    return '/photos';
  }

  // Get photos with advanced filtering
  async getPhotos(query: PhotoQuery = {}, projectId?: string) {
    try {
      const params = new URLSearchParams();

      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const endpoint = this.getPhotosEndpoint(projectId);
      const response = await api.get(`${endpoint}?${params.toString()}`);

      // Handle different response structures gracefully
      const responseData = response.data.data || response.data;

      return {
        success: true,
        photos: (responseData.photos || responseData || []) as ProjectPhoto[],
        total: responseData.total || 0,
        page: responseData.page || 1,
        totalPages: responseData.totalPages || 1,
      };
    } catch (error) {
      console.error('Failed to fetch photos:', error);
      return {
        success: false,
        message: 'Failed to load photos',
        photos: [],
        total: 0,
        page: 1,
        totalPages: 1,
      };
    }
  }

  // Get projects for dropdown (dynamic)
  async getProjectsForDropdown() {
    try {
      const response = await api.get('/projects/dropdown');
      const responseData = response.data.data || response.data;
      return (responseData || []).map((project: any) => ({
        value: project.id,
        label: project.name,
      }));
    } catch (error) {
      console.error('Failed to fetch projects for dropdown:', error);
      return [];
    }
  }

  // Get photo-related configurations using bulk API
  async getPhotoConfigurations() {
    try {
      const result = await configService.getBulkConfigurations([
        'photo-categories',
        'photo-sort-options',
      ]);

      if (result.success && result.data) {
        const categories = (result.data['photo-categories'] || []).map((option: any) => ({
          value: option.value,
          label: option.label,
          color: this.getCategoryColor(option.value),
        }));

        const sortOptions = (result.data['photo-sort-options'] || []).map((option: any) => ({
          value: option.value,
          label: option.label,
        }));

        return { categories, sortOptions };
      }

      // Return empty arrays if bulk fetch fails
      return { categories: [], sortOptions: [] };
    } catch (error) {
      console.error('Failed to fetch photo configurations:', error);
      return { categories: [], sortOptions: [] };
    }
  }

  // Get photo categories from configuration (dynamic)
  async getPhotoCategories() {
    const { categories } = await this.getPhotoConfigurations();
    return categories;
  }

  // Get sort options from configuration
  async getSortOptions() {
    const { sortOptions } = await this.getPhotoConfigurations();
    return sortOptions;
  }

  // Get photo permissions for current user
  async getPhotoPermissions() {
    try {
      const response = await api.get('/photos/permissions');

      // Handle different response structures gracefully
      const responseData = response.data.data || response.data;
      return responseData;
    } catch (error) {
      console.error('Failed to fetch photo permissions:', error);
      return {
        view: true,
        upload: true,
        edit: true,
        delete: false,
        download: true,
        bulk_download: true,
        annotate: true,
      };
    }
  }

  // Helper method to get category colors
  private getCategoryColor(category: string): string {
    const colorMap: Record<string, string> = {
      progress: 'bg-red-100 text-red-800',
      safety: 'bg-red-100 text-red-800',
      inspection: 'bg-yellow-100 text-yellow-800',
      material_delivery: 'bg-green-100 text-green-800',
      issue: 'bg-red-100 text-red-800',
      quality_control: 'bg-purple-100 text-purple-800',
      before_after: 'bg-indigo-100 text-indigo-800',
      documentation: 'bg-gray-100 text-gray-800',
      completion: 'bg-green-100 text-green-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return colorMap[category] || 'bg-gray-100 text-gray-800';
  }

  // Bulk operations
  async bulkDeletePhotos(photoIds: string[], projectId?: string) {
    try {
      const endpoint = this.getPhotosEndpoint(projectId);
      const response = await api.post(`${endpoint}/bulk-delete`, { photo_ids: photoIds });
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to bulk delete photos:', error);
      return {
        success: false,
        message: 'Failed to delete photos',
      };
    }
  }

  // Get single photo by ID
  async getPhoto(photoId: string, projectId?: string) {
    try {
      const endpoint = this.getPhotosEndpoint(projectId);
      const response = await api.get(`${endpoint}/${photoId}`);
      return response.data as ProjectPhoto;
    } catch (error) {
      console.error('Failed to fetch photo:', error);
      throw error;
    }
  }

  // Optimized upload with better error handling and progress tracking
  async uploadPhotos(
    uploadData: PhotoUploadData,
    onProgress?: (progress: number) => void,
    projectId?: string
  ) {
    try {
      // Validate upload data before processing
      if (!uploadData.files || uploadData.files.length === 0) {
        throw new Error('No files selected for upload');
      }

      if (uploadData.files.length > 10) {
        throw new Error('Maximum 10 files allowed per upload');
      }

      // Step 1: Upload files to S3 using the bulk upload API directly
      const formData = new FormData();

      // Add all files to FormData with validation
      uploadData.files.forEach((file, index) => {
        if (!file.type.startsWith('image/')) {
          throw new Error(`File ${index + 1} is not an image`);
        }
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`File ${index + 1} exceeds 10MB limit`);
        }
        formData.append('files', file);
      });

      // Add metadata
      formData.append('projectId', uploadData.project_id);
      formData.append('module', 'photos');

      // Use the bulk upload endpoint directly
      const response = await api.post('/files/upload-multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: progressEvent => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'File upload failed');
      }

      // Extract file references from successful uploads
      const fileReferences = response.data.data;

      // Validate file references structure
      if (!Array.isArray(fileReferences) || fileReferences.length === 0) {
        throw new Error('Invalid file references received from upload service');
      }

      // Validate each file reference has required fields
      for (const fileRef of fileReferences) {
        if (!fileRef.file_url || !fileRef.file_size || !fileRef.mime_type) {
          console.error('Invalid file reference:', fileRef);
          throw new Error(
            'File reference missing required fields (file_url, file_size, mime_type)'
          );
        }
      }

      // Step 2: Create photo records with file references
      const photoData = {
        project_id: uploadData.project_id,
        title: uploadData.title,
        description: uploadData.description || '',
        category: uploadData.category,
        tags: Array.isArray(uploadData.tags) ? uploadData.tags : [],
        location: {
          site: uploadData.location.site || '',
          building: uploadData.location.building || '',
          floor: uploadData.location.floor || '',
          room: uploadData.location.room || '',
          gps: uploadData.location.gps || '',
        },
        number_on_image: uploadData.number_on_image || '',
        file_references: fileReferences, // S3 file references
      };

      const endpoint = this.getPhotosEndpoint(projectId);

      // Debug logging
      console.log('Creating photos with data:', {
        endpoint: `${endpoint}/create-with-files`,
        photoData: {
          ...photoData,
          file_references: `${fileReferences.length} file references`,
        },
      });

      const photoResponse = await api.post(`${endpoint}/create-with-files`, photoData);

      if (!photoResponse.data.success) {
        throw new Error(photoResponse.data.message || 'Failed to create photo records');
      }

      return {
        success: true,
        data: photoResponse.data.data as ProjectPhoto[],
        message: photoResponse.data.message || 'Photos uploaded successfully',
      };
    } catch (error: any) {
      console.error('Failed to upload photos:', error);
      return {
        success: false,
        message: error.message || 'Failed to upload photos',
      };
    }
  }

  // Update photo metadata
  async updatePhoto(photoId: string, updates: Partial<ProjectPhoto>, projectId?: string) {
    try {
      const endpoint = this.getPhotosEndpoint(projectId);
      const response = await api.put(`${endpoint}/${photoId}`, updates);
      return response.data as ProjectPhoto;
    } catch (error) {
      console.error('Failed to update photo:', error);
      throw error;
    }
  }

  // Delete photo
  async deletePhoto(photoId: string, projectId?: string) {
    try {
      const endpoint = this.getPhotosEndpoint(projectId);
      await api.delete(`${endpoint}/${photoId}`);
      return {
        success: true,
        message: 'Photo deleted successfully',
      };
    } catch (error) {
      console.error('Failed to delete photo:', error);
      return {
        success: false,
        message: 'Failed to delete photo',
      };
    }
  }

  // Download single photo
  async downloadPhoto(photoId: string, fileName: string, projectId?: string) {
    try {
      const endpoint = this.getPhotosEndpoint(projectId);
      const response = await api.get(`${endpoint}/${photoId}/download`, {
        responseType: 'blob',
      });

      this.triggerDownload(response.data, fileName);
    } catch (error) {
      console.error('Failed to download photo:', error);
      throw error;
    }
  }

  // Bulk download photos
  async bulkDownload(photoIds: string[], fileName = 'photos.zip', projectId?: string) {
    try {
      const endpoint = this.getPhotosEndpoint(projectId);
      const response = await api.post(
        `${endpoint}/bulk-download`,
        { photo_ids: photoIds },
        { responseType: 'blob' }
      );

      this.triggerDownload(response.data, fileName);
    } catch (error) {
      console.error('Failed to bulk download photos:', error);
      throw error;
    }
  }

  // Get photo statistics
  async getPhotoStats(projectId?: string) {
    try {
      const endpoint = this.getPhotosEndpoint(projectId);
      console.log('Fetching photo stats from endpoint:', `${endpoint}/stats`);

      const response = await api.get(`${endpoint}/stats`);
      console.log('Photo stats response:', response.data);

      // Handle different response structures gracefully
      const responseData = response.data.data || response.data;

      // Ensure the response matches PhotoStats interface
      const stats = {
        total_photos: responseData.total_photos || 0,
        by_category: responseData.by_category || {},
        recent_uploads: responseData.recent_uploads || 0,
        storage_used: responseData.storage_used || 0,
      } as PhotoStats;

      console.log('Processed photo stats:', stats);
      return stats;
    } catch (error: any) {
      console.error('Failed to fetch photo stats:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      // Return default stats structure instead of throwing
      return {
        total_photos: 0,
        by_category: {
          progress: 0,
          safety: 0,
          inspection: 0,
          material_delivery: 0,
          issue: 0,
          quality_control: 0,
          before_after: 0,
          documentation: 0,
          completion: 0,
          other: 0,
        },
        recent_uploads: 0,
        storage_used: 0,
      } as PhotoStats;
    }
  }

  // Add annotation to photo
  async addAnnotation(
    photoId: string,
    annotation: Omit<PhotoAnnotation, 'id' | 'created_by' | 'created_at'>
  ) {
    try {
      const response = await api.post(`/photos/${photoId}/annotations`, annotation);
      return response.data as PhotoAnnotation;
    } catch (error) {
      console.error('Failed to add annotation:', error);
      throw error;
    }
  }

  // Update annotation
  async updateAnnotation(photoId: string, annotationId: string, updates: Partial<PhotoAnnotation>) {
    try {
      const response = await api.put(`/photos/${photoId}/annotations/${annotationId}`, updates);
      return response.data as PhotoAnnotation;
    } catch (error) {
      console.error('Failed to update annotation:', error);
      throw error;
    }
  }

  // Delete annotation
  async deleteAnnotation(photoId: string, annotationId: string) {
    try {
      await api.delete(`/photos/${photoId}/annotations/${annotationId}`);
    } catch (error) {
      console.error('Failed to delete annotation:', error);
      throw error;
    }
  }

  // Create new version of existing photo
  async createPhotoVersion(originalPhotoId: string, file: File, updates?: Partial<ProjectPhoto>) {
    try {
      const formData = new FormData();
      formData.append('photo', file);

      if (updates) {
        formData.append('updates', JSON.stringify(updates));
      }

      const response = await api.post(`/photos/${originalPhotoId}/versions`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data as ProjectPhoto;
    } catch (error) {
      console.error('Failed to create photo version:', error);
      throw error;
    }
  }

  // Get photo versions/history
  async getPhotoVersions(taggingId: string) {
    try {
      const response = await api.get(`/photos/versions/${taggingId}`);
      return response.data as ProjectPhoto[];
    } catch (error) {
      console.error('Failed to fetch photo versions:', error);
      throw error;
    }
  }

  // Link photo to task/milestone/RFI
  async linkPhoto(
    photoId: string,
    linkData: {
      task_id?: string;
      milestone_id?: string;
      rfi_id?: string;
      issue_id?: string;
    }
  ) {
    try {
      const response = await api.post(`/photos/${photoId}/link`, linkData);
      return response.data as ProjectPhoto;
    } catch (error) {
      console.error('Failed to link photo:', error);
      throw error;
    }
  }

  // Search photos by advanced criteria
  async searchPhotos(searchTerm: string, filters: Partial<PhotoQuery> = {}, projectId?: string) {
    try {
      const query = {
        search: searchTerm,
        ...filters,
      };
      return this.getPhotos(query, projectId);
    } catch (error) {
      console.error('Failed to search photos:', error);
      throw error;
    }
  }

  // Get photos for timeline view
  async getPhotoTimeline(projectId: string, dateRange?: { from: string; to: string }) {
    try {
      const params = new URLSearchParams({ project_id: projectId });
      if (dateRange) {
        params.append('date_from', dateRange.from);
        params.append('date_to', dateRange.to);
      }

      const response = await api.get(`/photos/timeline?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch photo timeline:', error);
      throw error;
    }
  }

  // Auto-generate tagging ID
  async generateTaggingId(projectId: string, category: string) {
    try {
      const response = await api.post('/photos/generate-tagging-id', {
        project_id: projectId,
        category: category,
      });
      return response.data.tagging_id as string;
    } catch (error) {
      console.error('Failed to generate tagging ID:', error);
      throw error;
    }
  }

  // Helper method to trigger file download
  private triggerDownload(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export default new PhotoService();
